import {t} from "@lingui/core/macro";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {
    markMinorConversions,
    usePublishAnalyticsEvent,
    type MinorConversionData,
} from "@khan/analytics-learning-time";
import {
    authType,
    calculateNextUrl,
    createReauthUrl,
    LoginButton,
    roleToUserRole,
    type AuthType,
    type LoginTypeData,
    type Role,
} from "@khan/auth-shared";
import {useGql} from "@khan/data-gql";
import Log from "@khan/logging";
import {preserveMarketingParams, redirect, useLocation} from "@khan/urls";

import {
    getErrorMessageForError,
    loginWithAppleAuthCodeAndState,
    reauthWithApple,
} from "./apple-graphql.ts";
import {AppleIcon} from "./apple-icon.tsx";
import {launchAppleConnection, type AppleLoginResponse} from "./apple-util.ts";

export interface CompleteLoginOverrideParamsApple {
    userData: LoginTypeData;
}

interface Props {
    disabled?: boolean;
    style?: StyleType;
    size?: "small" | "medium" | "large";
    continueUrl?: string;
    role?: Role;
    minorConversions?: ReadonlyArray<MinorConversionData>;
    classCodes?: Array<string>;
    // Callback for when doLogin succeeds, overrides the completeLogin behavior
    //
    // If this is not provided, completeLogin will be executed as normal
    //
    // If this is provided, the passed function will be executed instead of completeLogin
    //
    // If your override function requires additional parameters, add them to the CompleteLoginOverrideParams type
    completeLoginOverride?: (
        options: CompleteLoginOverrideParamsApple,
    ) => Promise<void>;
    /**
     * Callback for when the login fails.
     *
     * This can be caused due to Sign in with Apple auth errors or errors
     * caused in the the Sign in with Apple GraphQL mutation.
     */
    onError?: (errorMessage: string) => unknown;
    // The handler to be executed when reauthentication is successful (to be
    // set only if the button is used for reauth)
    onReauthSuccess?: (arg1: string) => unknown;
    /**
     * Callback for when the reauth flow fails.
     *
     * This is executed when the re-authentication with Apple fails.
     */
    onReauthError?: (url: string) => unknown;
    /**
     * Event Type for the CEDAR event which will be published on click
     *
     * We only pass the event type and rely on this component to determine
     * the rest of the event data.
     *
     * NOTE: If you update the list of possible values for this type, make sure
     * to update `handleClick` to publish the correct event.
     */
    cedarEventType?: "SignupAppleButtonClicked";
    iconOnly?: boolean;
    /**
     * Whether form validation is required before executing the login logic
     */
    validationRequired?: boolean;
    /**
     * Whether the current form is valid. Determined by the TOS checkbox
     */
    isFormValid?: boolean;
    /**
     * Called when the form is invalid and validation is required.
     */
    onValidationError?: () => void;
    /**
     * Whether button is SIGNUP or LOGIN
     */
    context?: AuthType;
}

/**
 * Handles the UI part of the Sign in with Apple integration.
 * @see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/535757033/Sign+in+with+Apple
 */
export function AppleButton(props: Props): React.ReactNode {
    const gqlFetch = useGql();
    const location = useLocation();
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const disableSignupViaOauthLoginFeatureFlagOn = useFlagOrCookie(
        "disable-signup-via-oauth-login",
    );

    // TODO(davemcalpine): carryover from old code, error, and loading weren't
    // used. Probably should change that.
    const [_, setError] = React.useState<Error | null>(null);
    const [__, setLoading] = React.useState(true);
    const [loggingIn, setLoggingIn] = React.useState(false);

    /**
     * Handles any errors from the Sign in with Apple API or from the Sign in
     * GraphQL mutation call.
     * @param {any} error The error thrown in the current context.
     */
    const handleError = (error?: any): void => {
        if (error?.message === "CANNOT_CREATE_NEW_ACCOUNT") {
            const errorMessage = t`We cannot find an account associated with that login. Please sign up for an account to continue.`;
            if (props.onError) {
                props.onError(errorMessage);
            }
        } else if (
            // The Sign in with Apple JS SDK raises this error in some cases
            // when you re-start the auth flow. But there's no reason to signal
            // an error so we ignore it here.
            // We also don't show the message for the SHADOWS_EXISTING_ACCOUNT
            // because it's handled by apple-graphql.js (we redirect away in
            // that but still throw an error).
            error?.message !== "SHADOWS_EXISTING_ACCOUNT" &&
            error?.error !== "user_trigger_new_signin_flow"
        ) {
            const errorMessage = getErrorMessageForError(error);

            Log.log("Something unexpected happened!", {
                code: error?.error,
                message: errorMessage,
            });

            if (props.onError) {
                props.onError(errorMessage);
            }
        }

        setError(null);
        setLoading(false);
        setLoggingIn(false);
    };

    const completeLogin = async (
        userData: LoginTypeData,
        continueUrl: string,
    ) => {
        const calculatedNextUrl = await calculateNextUrl({
            gqlFetch: gqlFetch,
            location: location,
            loginType: "apple",
            continueUrl,
            isFirstLogin: userData.isFirstLogin,
            joinClassrooms: userData.joinClassrooms,
        });

        redirect(
            preserveMarketingParams(
                calculatedNextUrl,
                new URLSearchParams(location.search),
            ),
        );
    };

    const doLogin = async (
        response: AppleLoginResponse,
        continueUrl: string,
    ) => {
        // Keep flow happy. It doesn't know we already refined the response
        // in the callsite of this function.
        if (response.nativeSignIn) {
            return;
        }

        // Complete new Apple login flow.
        // Get Apple info, if returned from the Apple Sign In API.
        const appleUserData =
            response.user && response.user.email && response.user.name
                ? {
                      email: response.user.email,
                      firstName: response.user.name.firstName,
                      lastName: response.user.name.lastName,
                  }
                : null;

        try {
            // Only pass context if signup check is enabled;
            // Skip "REAUTH" because it's not a valid flowType for the mutation
            const contextValue = disableSignupViaOauthLoginFeatureFlagOn
                ? props.context === authType.REAUTH
                    ? undefined
                    : props.context
                : undefined;

            const userData = await loginWithAppleAuthCodeAndState(gqlFetch, {
                authCode: response.authorization.code,
                userData: appleUserData,
                state: response.authorization.state,
                signupCodes: props.classCodes,
                role: props.role,
                context: contextValue,
            });

            if (props.completeLoginOverride) {
                props.completeLoginOverride({userData});
            } else {
                await completeLogin(userData, continueUrl);
            }
        } catch (error: any) {
            handleError(error);
        }
    };

    const doReauth = async (response: AppleLoginResponse) => {
        // Keep flow happy. It doesn't know we already refined the response
        // in the callsite of this function.
        if (response.nativeSignIn) {
            return;
        }

        try {
            await reauthWithApple(
                gqlFetch,
                response.authorization.code,
                response.authorization.state,
            );
            // get redirect URL that will display the success message.
            const reauthUrl = createReauthUrl({loginType: "apple"});

            // execute onSuccess callback passed as a prop.
            if (props.onReauthSuccess) {
                props.onReauthSuccess(reauthUrl);
            }

            setLoading(false);
        } catch (error: any) {
            Log.log("Something unexpected happened!", {
                code: error.message,
            });
            // get redirect URL that will display the error message.
            const reauthUrlError = createReauthUrl({
                loginType: "apple",
                error: error.message,
            });

            if (props.onReauthError) {
                props.onReauthError(reauthUrlError);
            }

            setLoading(false);
        }
    };

    const handleClick = (e: React.SyntheticEvent) => {
        const {
            minorConversions,
            onReauthSuccess,
            continueUrl,
            classCodes,
            onValidationError,
            isFormValid,
            validationRequired,
        } = props;

        if (validationRequired && !isFormValid) {
            // Trigger error callback for invalid form when validation is required
            onValidationError?.();
            return;
        }

        setError(null);
        setLoading(true);

        if (props.cedarEventType === "SignupAppleButtonClicked") {
            publishAnalyticsEvent(
                {
                    eventType: "SignupAppleButtonClicked",
                    eventSchemaVersion: 1,
                },
                // NOTE: we cannot add the legacy bingo conversion id here
                // it's a minor conversion.
            );
        }

        if (minorConversions) {
            markMinorConversions(minorConversions);
        }

        const isReauth = !!onReauthSuccess;
        const action = isReauth ? "REAUTH" : "LOGIN";
        const role = roleToUserRole(props.role);

        // Use Sign in with Apple JS.
        launchAppleConnection(
            gqlFetch,
            action,
            continueUrl || "/",
            role,
            classCodes,
        )
            .then((response) => {
                if (response.nativeSignIn) {
                    return;
                }

                // Notify the user via UI that it's being logged in.
                setLoggingIn(true);

                onReauthSuccess
                    ? doReauth(response)
                    : doLogin(response, continueUrl || "/");
            })
            .catch((error) => {
                handleError(error);
            });
    };

    return (
        <LoginButton
            title={!loggingIn ? t`Continue with Apple` : t`Logging you in...`}
            iconComponent={AppleIcon}
            disabled={props.disabled}
            onClick={handleClick}
            style={props.style}
            size={props.size}
            iconOnly={props.iconOnly}
        />
    );
}
